import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const QK = "vi_offline_queue";
const CK = "vi_cache_";
const listeners = new Set();

export const isNetErr = (e) => !e?.response;
const userId = () => { try { return JSON.parse(localStorage.getItem("omnibat_user") || "null")?.id || ""; } catch { return ""; } };
const allOps = () => { try { return JSON.parse(localStorage.getItem(QK) || "[]"); } catch { return []; } };
export const getQueue = () => allOps().filter((o) => o.user_id === userId());
const setAll = (q) => { localStorage.setItem(QK, JSON.stringify(q)); listeners.forEach((f) => f()); };
export const subscribe = (f) => { listeners.add(f); return () => listeners.delete(f); };

export const readCache = (k) => { try { return JSON.parse(localStorage.getItem(CK + k)); } catch { return null; } };
export const writeCache = (k, v) => { try { localStorage.setItem(CK + k, JSON.stringify(v)); } catch { /* quota */ } };

// GET avec repli sur la dernière copie locale quand le réseau est coupé
export async function cachedGet(path) {
  try { const r = await api.get(path); writeCache(path, r.data); return r.data; }
  catch (e) { const c = readCache(path); if (isNetErr(e) && c != null) return c; throw e; }
}

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function patchTour(t, op) {
  if (!t || t.id !== op.tour_id) return t;
  const p = op.payload;
  if (op.type === "sale") {
    let total = 0;
    const stock = (t.mobile_stock || []).map((s) => {
      const ln = p.lines.find((l) => l.article_id === s.article_id);
      if (!ln) return s;
      total += s.prix * ln.quantite * (1 - (ln.remise || 0) / 100);
      return { ...s, restant: s.restant - ln.quantite, vendu: s.vendu + ln.quantite };
    });
    const paye = p.payments.reduce((a, x) => a + (Number(x.montant) || 0), 0);
    const sale = { id: `local:${op.op_id}`, numero: "HORS LIGNE", client_name: op.label, total, paye, credit: Math.max(0, total - paye),
      statut: "En attente sync", created_at: op.created_at, lines: p.lines, payments: p.payments };
    return { ...t, mobile_stock: stock, sales: [sale, ...(t.sales || [])], sales_count: (t.sales_count || 0) + 1, ca: (t.ca || 0) + total };
  }
  if (op.type === "checkin") {
    const v = { id: `local:${op.op_id}`, numero: "HORS LIGNE", cible: op.label, checkin_at: op.created_at, statut: "En cours", tournee_id: t.id };
    return { ...t, visits: [v, ...(t.visits || [])], visites: (t.visites || 0) + 1 };
  }
  if (op.type === "checkout") {
    const vid = p.visit_id || `local:${p.visit_op_id}`;
    return { ...t, visits: (t.visits || []).map((v) => (v.id === vid ? { ...v, statut: "Terminée", checkout_at: op.created_at, resultat: p.resultat } : v)) };
  }
  if (op.type === "quote") {
    const q = { id: `local:${op.op_id}`, numero: "HORS LIGNE", client_name: op.label, total_ttc: op.total || 0, statut: "En attente sync" };
    return { ...t, quotes: [q, ...(t.quotes || [])] };
  }
  return t;
}

function applyLocal(op) {
  const mt = readCache("/itinerant/my-tour");
  if (mt?.tour) writeCache("/itinerant/my-tour", { tour: patchTour(mt.tour, op) });
  const key = `/itinerant/tournees/${op.tour_id}`;
  const td = readCache(key);
  if (td) writeCache(key, patchTour(td, op));
}

// Exécute en direct (idempotent) ; met en file d'attente si le réseau est indisponible
export async function runOp(type, tourId, payload, label, extra = {}) {
  const op = { op_id: uid(), type, tour_id: tourId, payload, label, created_at: new Date().toISOString(), user_id: userId(), ...extra };
  if (navigator.onLine) {
    try {
      const { user_id, total, ...body } = op; // eslint-disable-line no-unused-vars
      const r = await api.post("/itinerant/sync", { ops: [body], live: true });
      return { queued: false, result: r.data.results[0] };
    } catch (e) { if (!isNetErr(e)) throw e; }
  }
  setAll([...allOps(), op]);
  applyLocal(op);
  return { queued: true, op };
}

let flushing = false;
export async function flush(manual = false) {
  const q = getQueue();
  if (!q.length || flushing || !localStorage.getItem("omnibat_token")) return;
  flushing = true;
  try {
    const r = await api.post("/itinerant/sync", { ops: q.map(({ user_id, total, ...o }) => o) }); // eslint-disable-line no-unused-vars
    const done = new Set(r.data.results.map((x) => x.op_id));
    setAll(allOps().filter((o) => !done.has(o.op_id)));
    const conf = r.data.results.filter((x) => x.statut === "conflit");
    const ok = r.data.results.length - conf.length;
    if (ok) toast.success(`${ok} opération(s) hors ligne synchronisée(s)`);
    if (conf.length) toast.error(`${conf.length} opération(s) en conflit — ${conf[0].detail}`, { duration: 8000 });
    window.dispatchEvent(new Event("vi-synced"));
  } catch (e) {
    if (manual) toast.error(isNetErr(e) ? "Toujours hors ligne : réessayez plus tard" : apiErr(e));
  } finally { flushing = false; }
}

let started = false;
export function startAutoSync() {
  if (started) return;
  started = true;
  window.addEventListener("online", () => flush());
  setInterval(() => { if (navigator.onLine) flush(); }, 30000);
  flush();
}
